import React from "react";
import {
  cn,
  getBackgroundStyles,
  getForegroundStyles,
  isPanelColor,
} from "../../styles";
import { BaseElementProps, ThemeColor, PanelColor } from "../../props";

/**
 * Positions available for the badge element relative to its parent.
 */
export type BadgePosition =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left";

export interface BadgeProps extends Omit<BaseElementProps, "className"> {
  /**
   * Color to be applied to the badge.
   * Can be a semantic theme color or hierarchical panel level color.
   * @default ThemeColor.primary
   */
  color?: ThemeColor | PanelColor;
  /**
   * The anchor node to which the badge should be applied to.
   */
  children?: React.ReactNode;

  /**
   * Additional CSS class name(s) applied to the wrapping root div.
   */
  className?: string;

  /**
   * Additional class name(s) for the badge element itself.
   * Use negative margin to offset from the parent box.
   */
  badgeClassName?: string;

  /**
   * Content to display inside the badge. Usually text or numbers but can be any React node.
   * @default null
   */
  content?: React.ReactNode;

  /**
   * Position of the badge relative to its parent.
   * @default "top-right"
   */
  position?: BadgePosition;
}

/**
 * Displays a small badge over its children, often used for notifications or status indicators.
 */
const colorStyles: Record<ThemeColor, string> = {
  primary: "bg-primary-500 text-front-50 dark:bg-primary-600",
  secondary: "bg-secondary-500 text-front-50 dark:bg-secondary-600",
  gray: "bg-back-400 text-front-50 dark:bg-back-600",
  error: "bg-error-600 text-front-50 dark:bg-error-700",
  warning: "bg-warning-600 text-front-50 dark:bg-warning-700",
  success: "bg-success-600 text-front-50 dark:bg-success-700",
  transparent: "bg-front-200/20 text-front-800 dark:bg-front-700/20 dark:text-front-100",
  none: "",
  danger: "bg-error-600 text-front-50 dark:bg-error-700",
  alert: "bg-warning-600 text-front-50 dark:bg-warning-700",
  done: "bg-success-600 text-front-50 dark:bg-success-700",
};

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      children,
      className,
      badgeClassName,
      color = ThemeColor.gray,
      content = null,
      position = "top-right",
    },
    ref
  ) => {
    const positionStyles: Record<BadgePosition, string> = {
      "top-right": "top-0 right-0 -translate-y-1/2 translate-x-1/2",
      "top-left": "top-0 left-0 -translate-y-1/2 -translate-x-1/2",
      "bottom-right": "bottom-0 right-0 translate-y-1/2 translate-x-1/2",
      "bottom-left": "bottom-0 left-0 translate-y-1/2 -translate-x-1/2",
    };

    return (
      <div ref={ref} className={`relative inline-block ${className ?? ""}`}>
        {children}
        {content !== null && (
          <span
            className={cn(
              positionStyles[position],
              isPanelColor(color)
                ? cn(getBackgroundStyles(color), getForegroundStyles(color))
                : colorStyles[color as ThemeColor],
              "absolute text-xs font-medium px-1.25 py-0.25 rounded-4xl -mx-1.25",
              badgeClassName
            )}
          >
            {content}
          </span>
        )}
      </div>
    );
  }
);

Badge.displayName = "Badge";
